/**
 * Les échéances administratives, adossées à Supabase.
 *
 * Un adaptateur sans finesse, et c'est voulu : la table ne se rattache à
 * aucune autre sauf pour le nom du chantier d'une retenue de garantie. Toute
 * la logique — l'urgence, le classement, la date suivante — vit dans `core`,
 * pure et éprouvée sans base.
 */
import { DomainError } from "@dker/core";
import type {
  AdminDeadline,
  DeadlineRepository,
  ExecutionContext,
  SaveDeadlineInput,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

/** Le nom du chantier arrive par la jointure, sous forme d'objet ou de tableau. */
function projectName(raw: unknown): string | null {
  if (raw == null) return null;
  const p = Array.isArray(raw) ? raw[0] : raw;
  return (p as Row | undefined)?.name ?? null;
}

function toDeadline(row: Row): AdminDeadline {
  return {
    id: row.id,
    kind: row.kind,
    label: row.label,
    dueOn: String(row.due_on).slice(0, 10),
    amount: row.amount == null ? null : Number(row.amount),
    reference: row.reference ?? null,
    notes: row.notes ?? null,
    recurrence: row.recurrence,
    projectId: row.project_id ?? null,
    projectName: projectName(row.projects),
  };
}

const COLONNES =
  "id, kind, label, due_on, amount, reference, notes, recurrence, project_id, projects(name)";

export function createSupabaseDeadlineRepository(
  clientFor: DbClientFactory,
): DeadlineRepository {
  async function read(ctx: ExecutionContext, id: string) {
    const { data, error } = await clientFor(ctx)
      .from("admin_deadlines")
      .select(COLONNES)
      .eq("id", id)
      .maybeSingle();
    if (error) fail(error);
    return data ? toDeadline(data as Row) : null;
  }

  return {
    read,

    async list(ctx: ExecutionContext, includePast: boolean) {
      let query = clientFor(ctx).from("admin_deadlines").select(COLONNES);
      if (!includePast) {
        // La borne se calcule en base plutôt que dans le navigateur : l'horloge
        // du poste peut être fausse, et une décennale expirée masquée par une
        // date de machine mal réglée serait exactement le défaut qu'on cherche
        // à éviter.
        query = query.gte("due_on", new Date().toISOString().slice(0, 10));
      }
      const { data, error } = await query.order("due_on", { ascending: true });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toDeadline);
    },

    async save(ctx: ExecutionContext, command: Omit<SaveDeadlineInput, "confirmationId">) {
      const ligne = {
        kind: command.kind,
        label: command.label,
        due_on: command.dueOn,
        amount: command.amount,
        reference: command.reference,
        notes: command.notes,
        recurrence: command.recurrence,
        project_id: command.projectId,
      };

      // `upsert` demanderait de fournir `tenant_id` à la création, alors que la
      // colonne le pose seule par défaut. Deux chemins explicites coûtent trois
      // lignes et évitent d'écrire un tenant à la main — ce qui, dans un socle
      // multi-tenant, est la ligne qu'on n'a pas envie d'écrire.
      const q = clientFor(ctx).from("admin_deadlines");
      const { data, error } = command.id
        ? await q.update(ligne).eq("id", command.id).select(COLONNES).single()
        : await q.insert(ligne).select(COLONNES).single();
      if (error) fail(error);
      return toDeadline(data as Row);
    },

    async remove(ctx: ExecutionContext, id: string) {
      const { error } = await clientFor(ctx).from("admin_deadlines").delete().eq("id", id);
      if (error) fail(error);
    },

    async moveDueDate(ctx: ExecutionContext, id: string, dueOn: string) {
      const { data, error } = await clientFor(ctx)
        .from("admin_deadlines")
        .update({ due_on: dueOn })
        .eq("id", id)
        .select(COLONNES)
        .single();
      if (error) fail(error);
      return toDeadline(data as Row);
    },
  };
}
